import { useMemo, useState } from 'react';

import {
  Icons,
  EntityAction,
  EntityActions,
  Typography,
  Stack,
} from '@cast/design-system';

import { Detail } from 'components/drawer';
import { NodeWithDependencies } from 'features/cluster/nodes/types';

import { NodeCost } from '../_components/NodeCost';
import { COST_PERIODS } from '../_constants/cost';
import { CostPeriod } from '../_types/cost';
import { calculateCostPerCPU, calculateInstanceCost } from '../_utils/nodeCost';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type NodeDetailsPriceProps = {
  price?: number;
  costPeriod?: CostPeriod;
};

const NodeDetailsPrice = ({ price, costPeriod }: NodeDetailsPriceProps) => {
  return (
    <NodeCost
      price={price}
      costPeriod={costPeriod}
      primaryTextProps={{ variant: 'P14R' }}
      suffixTextProps={{ fontSize: '1em' }}
    />
  );
};

type Props = {
  node?: NodeWithDependencies;
};

export const NodeCostSection = ({ node }: Props) => {
  const [costPeriod, setCostPeriod] = useState<CostPeriod>(COST_PERIODS[0]);
  const { isCastAnywhere } = useCluster();

  const costPerCPU = useMemo(
    () => calculateCostPerCPU({ node, costPeriod: costPeriod, isCastAnywhere }),
    [node, costPeriod, isCastAnywhere]
  );

  const instanceCost = useMemo(
    () => calculateInstanceCost({ node, costPeriod: costPeriod }),
    [node, costPeriod]
  );

  return (
    <div>
      <Stack
        mb={12}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="L12B">COST</Typography>
        <EntityActions
          triggerButtonProps={{
            children: <Icons.DotsThree />,
          }}
        >
          {COST_PERIODS.map((item) => (
            <EntityAction
              key={item}
              selected={costPeriod === item}
              onClick={() => setCostPeriod(item)}
            >
              <Typography
                variant="P12R"
                color={costPeriod === item ? 'blue.700' : 'inherit'}
              >
                {item}{' '}
                <Typography
                  variant="inherit"
                  color="textSecondary"
                  component="span"
                >
                  /{item[0]}
                </Typography>
              </Typography>
            </EntityAction>
          ))}
        </EntityActions>
      </Stack>

      <Detail
        title="Instance Cost:"
        testId="node-details-instance-cost"
        detail={
          <NodeDetailsPrice price={instanceCost} costPeriod={costPeriod} />
        }
      />
      <Detail
        title="Cost per CPU:"
        testId="node-details-cost-per-cpu"
        detail={<NodeDetailsPrice price={costPerCPU} costPeriod={costPeriod} />}
      />
    </div>
  );
};
